/* <home-looks> — the looks gallery of the homepage (sections/home-looks.liquid, styles in home.css).

   State lives in classes; CSS does all the motion:
   - .hs-look-img.is-active / .is-leaving + data-dir on the root → clip-wipe reveal and push-aside,
   - .hs-look-list.is-active → rows rise in one by one,
   - .hs-looks__seg.is-active::after → the progress bar; its animationend advances the gallery (autoplay).
   Autoplay pauses (class is-paused) on keyboard focus inside, when the gallery is off-screen and when the tab is hidden; it is off
   entirely with "reduce motion". Arrows, dots, ← / → keys and swipe navigate. */
(() => {
  if (customElements.get('home-looks')) return;
  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
  const pad = (n) => String(n).padStart(2, '0');
  const restart = (el, cls) => { el.classList.remove(cls); void el.offsetWidth; el.classList.add(cls); };

  class HomeLooks extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = '1';
      this.lists = [...this.querySelectorAll('.hs-look-list')];
      this.imgs = [...this.querySelectorAll('.hs-look-img')];
      this.segs = [...this.querySelectorAll('.hs-looks__seg')];
      this.n = this.lists.length;
      this.i = 0;
      this.section = this.closest('.hs-looks-section');
      if (this.n < 2) return;

      this.auto = this.dataset.autoplay === 'true' && !REDUCED.matches;
      this.classList.toggle('is-auto', this.auto);
      this.pauses = new Set(['offscreen']); // start paused until the gallery is seen

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.step(1));
      this.segs.forEach((seg) => seg.addEventListener('click', () => {
        const to = +seg.dataset.go;
        if (to !== this.i) this.go(to, to > this.i ? 'next' : 'prev');
      }));
      this.querySelector('.hs-looks__progress')?.addEventListener('animationend', (e) => {
        if (e.animationName === 'hsLookProgress' && this.auto) this.step(1);
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
      });

      // swipe / drag on the photo
      const stage = this.querySelector('.hs-looks__stage');
      let x0 = null;
      stage?.addEventListener('pointerdown', (e) => { x0 = e.clientX; });
      stage?.addEventListener('pointerup', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0; x0 = null;
        if (Math.abs(dx) > 40) this.step(dx < 0 ? 1 : -1);
      });
      stage?.addEventListener('pointercancel', () => { x0 = null; });

      // pause reasons. No pause on mouse hover: the product list fills most of the block, so a resting
      // cursor stopped the progress bar until it moved away, which looked like a frozen gallery.
      this.addEventListener('focusin', (e) => { if (e.target.matches(':focus-visible')) this.pause('focus'); });
      this.addEventListener('focusout', () => this.resume('focus'));
      document.addEventListener('visibilitychange', () => (document.hidden ? this.pause('tab') : this.resume('tab')));
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => entries.forEach((en) => {
          if (en.isIntersecting) { this.preload(); this.resume('offscreen'); } else { this.pause('offscreen'); }
        }), { threshold: 0.35, rootMargin: '0px 0px 300px 0px' }).observe(this);
      } else { this.preload(); this.resume('offscreen'); }
      this.sync();
    }

    pause(reason) { this.pauses.add(reason); this.sync(); }
    resume(reason) { this.pauses.delete(reason); this.sync(); }
    sync() { this.classList.toggle('is-paused', this.pauses.size > 0); }

    step(d) { this.go((this.i + d + this.n) % this.n, d > 0 ? 'next' : 'prev'); }

    // look photos 2…N are lazy in the markup: start them all once the gallery is seen,
    // so the first switch does not wipe in an empty frame and pop the photo in later
    preload() {
      if (this.preloaded) return;
      this.preloaded = true;
      this.imgs.forEach((box) => box.querySelectorAll('img').forEach((img) => { img.loading = 'eager'; }));
    }

    // resolves when the photo of look `to` can be painted (or after 1.2 s on a slow connection)
    ready(to) {
      const img = this.imgs[to]?.querySelector('img');
      if (!img || (img.complete && img.naturalWidth)) return Promise.resolve();
      img.loading = 'eager';
      const decoded = img.decode ? img.decode().catch(() => {}) : new Promise((r) => { img.onload = img.onerror = r; });
      return Promise.race([decoded, new Promise((r) => setTimeout(r, 1200))]);
    }

    async go(to, dir) {
      if (to === this.i || !this.lists[to] || this.busy) return;
      this.busy = true;
      this.pause('loading');
      await this.ready(to);
      this.resume('loading');
      this.busy = false;
      const from = this.i;
      this.i = to;
      this.dataset.dir = dir;

      // photo: previous one stays underneath while the new one wipes in
      this.imgs.forEach((el) => el.classList.remove('is-leaving'));
      this.imgs[from].classList.remove('is-active');
      this.imgs[from].classList.add('is-leaving');
      restart(this.imgs[to], 'is-active');
      clearTimeout(this.leaveTimer);
      this.leaveTimer = setTimeout(() => this.imgs.forEach((el) => el.classList.remove('is-leaving')), REDUCED.matches ? 0 : 1000);

      // products of the look
      const prev = this.lists[from];
      prev.classList.remove('is-active');
      prev.setAttribute('aria-hidden', 'true');
      prev.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
      const next = this.lists[to];
      restart(next, 'is-active');
      next.removeAttribute('aria-hidden');
      next.querySelectorAll('a').forEach((a) => a.removeAttribute('tabindex'));

      // progress: earlier looks full, current one running
      this.segs.forEach((seg, k) => { seg.classList.toggle('is-done', k < to); seg.classList.remove('is-active'); });
      if (this.segs[to]) restart(this.segs[to], 'is-active');

      // caption, head note, button, counter, screen-reader note
      const caption = next.dataset.caption || '';
      const cap = this.querySelector('[data-look-caption]');
      if (cap) cap.textContent = caption;
      const aside = this.section?.querySelector('.hs-looks__aside-text');
      if (aside) aside.textContent = caption;
      const ctaWrap = this.querySelector('.hs-looks__cta');
      const cta = this.querySelector('[data-look-cta]');
      if (ctaWrap && cta) {
        const has = next.dataset.link && next.dataset.linkLabel;
        ctaWrap.hidden = !has;
        if (has) { cta.href = next.dataset.link; cta.textContent = next.dataset.linkLabel; }
      }
      const cur = this.querySelector('[data-look-current]');
      if (cur) cur.textContent = pad(to + 1);
      const live = this.querySelector('[data-look-live]');
      if (live) live.textContent = `Образ ${to + 1} з ${this.n}${caption ? ': ' + caption : ''}`;
      this.classList.remove('is-switching'); void this.offsetWidth; this.classList.add('is-switching');
      this.section?.classList.remove('is-switching'); void this.section?.offsetWidth; this.section?.classList.add('is-switching');
    }
  }
  customElements.define('home-looks', HomeLooks);
})();
